import React, { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';

import TaskContext from '@totem/components/ticketing/details/tasks/taskContext';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import BulkTimeEntryContext from '@totem/components/ticketing/details/timeEntry/bulkTimeEntry/bulkTimeEntryContext';
import {
  HierarchicalBulkTimeEntry,
  TaskTimeEntryItem,
  TimeEntryCommon,
} from '@totem/components/ticketing/details/timeEntry/bulkTimeEntry/types';
import {
  buildTaskTimeEntryHierarchy,
  sortTasksForUIDisplay,
} from '@totem/components/ticketing/utilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { sortNumberAscending } from '@totem/utilities/tableUtilities';

type Props = {
  children?: React.ReactNode;
};

const BulkTimeEntryContainer = ({ children }: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const { ticket } = useContext(TicketContext);
  const { resources } = useContext(TaskContext);

  const userResoruce = isNotNull(resources)
    ? resources.find((chk) => chk.email === userProfile.email)
    : null;
  const userResourceId = isNotNull(userResoruce) ? userResoruce?.id : '';

  const [taskTimeHierarchyItems, setTaskTimeHierarchyItems] = useState<
    HierarchicalBulkTimeEntry[]
  >([]);
  const [taskTimeItems, setTaskTimeItems] = useState<TaskTimeEntryItem[]>([]);
  const [timeEntryCommon, setTimeEntryCommon] = useState<TimeEntryCommon>({
    resourceId: userResourceId,
    date: dayjs().toISOString(),
    billable: !!(
      isNotNull(ticket) &&
      isNotNull(ticket.ticket.project) &&
      ticket.ticket.project.billingType === 'Time & Material'
    ),
  });

  useEffect(() => {
    if (
      isNotNull(ticket) &&
      isNotNull(ticket.ticket) &&
      isNotNull(ticket.ticket.tasks)
    ) {
      const newTasks = ticket.ticket.tasks
        .filter((chk) => !chk.isDeleted)
        .sort((compA, compB) =>
          sortNumberAscending(compA.number, compB.number),
        );

      const newActiveTask = sortTasksForUIDisplay(newTasks);
      const newTaskHierarchy = buildTaskTimeEntryHierarchy(newActiveTask);

      const newTaskTimeItems = newActiveTask.map((item) => {
        return {
          ...item,
          timeEntry: {
            hoursLogged: 0,
            billable: timeEntryCommon.billable,
            description: '',
          },
        };
      });

      setTaskTimeHierarchyItems(newTaskHierarchy);

      setTaskTimeItems(newTaskTimeItems);
    }
  }, [ticket?.ticket?.tasks]);

  const updateTaskHours = (taskId: string, newHours: number) => {
    setTaskTimeItems((prevItems) => {
      return prevItems.map((item) => {
        // Check if the current item is the one we want to update
        if (item.id === taskId) {
          // Return a new object with the updated timeEntry
          return {
            ...item,
            timeEntry: {
              ...item.timeEntry,
              hoursLogged: newHours,
            },
          };
        }
        // Return the item unchanged if it's not the target
        return item;
      });
    });
  };

  const updateTaskBillable = (taskId: string, newBillable: boolean) => {
    setTaskTimeItems((prevItems) => {
      return prevItems.map((item) => {
        // Check if the current item is the one we want to update
        if (item.id === taskId) {
          // Return a new object with the updated timeEntry
          return {
            ...item,
            timeEntry: {
              ...item.timeEntry,
              billable: newBillable,
            },
          };
        }
        // Return the item unchanged if it's not the target
        return item;
      });
    });
  };

  const updateAllTaskBillable = (newBillable: boolean) => {
    setTaskTimeItems((prevItems) => {
      return prevItems.map((item) => {
        // Return a new object with the updated timeEntry
        return {
          ...item,
          timeEntry: {
            ...item.timeEntry,
            billable: newBillable,
          },
        };
      });
    });
  };

  const updateTaskDescription = (taskId: string, newDescription: string) => {
    setTaskTimeItems((prevItems) => {
      return prevItems.map((item) => {
        // Check if the current item is the one we want to update
        if (item.id === taskId) {
          // Return a new object with the updated timeEntry
          return {
            ...item,
            timeEntry: {
              ...item.timeEntry,
              description: newDescription,
            },
          };
        }
        // Return the item unchanged if it's not the target
        return item;
      });
    });
  };

  const getTimeEntry = (taskId: string) => {
    const taskTimeEntryItem = taskTimeItems.find((task) => task.id === taskId);
    if (isNotNull(taskTimeEntryItem)) {
      return taskTimeEntryItem.timeEntry;
    }
    return {
      hoursLogged: 0,
      billable: false,
      description: '',
    };
  };

  return (
    <BulkTimeEntryContext.Provider
      value={{
        taskTimeEntryHierarchy: taskTimeHierarchyItems,
        taskTimeItems,
        timeEntryCommon,
        onUpdateTaskHours: updateTaskHours,
        onUpdateTaskBillable: updateTaskBillable,
        onUpdateAllTaskBillable: updateAllTaskBillable,
        onUpdateTaskDescription: updateTaskDescription,
        getTimeEntry,
        setTimeEntryCommon,
      }}
    >
      {children}
    </BulkTimeEntryContext.Provider>
  );
};

export default BulkTimeEntryContainer;
